import {
  DisplayGroups,
  type EditorElement,
} from '@wix/component-protocol/schema';
import { PrivateAPINamespace } from '../PrivateAPINamespace';

import {
  HostColorSelectorAPI,
  type ISelectColorParams,
  HostFilterSelectorAPI,
  type ISelectFilterParams,
  HostFontWeightSelectorAPI,
  type ISelectFontWeightParams,
  type ISelectFontParams,
  HostLinkSelectorAPI,
  type ISelectLinkParams,
  HostMediaSelectorAPI,
  type ISelectMediaParams,
  HostSortSelectorAPI,
  type ISelectSortParams,
} from './selectors';
import { SelectTextThemeOptions } from '@wix/editor-platform-sdk-types';
import { OperationSource } from '@wix/editor-platform-manifest-services';
import {
  type BasePickerOptions,
  type ElementLink,
  type ElementData,
  type ElementDataInput,
  type ElementStyles,
  type ElementStylesUpdate,
  type ElementStylesRemoval,
  type CssPropertyType,
  type ElementSort,
  type ElementFilter,
  DataType,
} from '@wix/public-editor-platform-interfaces';
import { PublicDataAdapter } from './PublicDataAdapter';
import { Link } from '@wix/document-services-types';
import { ApiConfig, Private } from '../../limitPrivateAPI';
import type {
  ColorPickerOptions,
  BackgroundPickerOptions,
} from '@wix/editor-platform-host-integration-apis';
import {
  createEditorPlatformPrivateAPIError,
  EditorPlatformPrivateAPIErrorCode,
} from '../../errors';

export class BuilderComponentNamespace extends PrivateAPINamespace {
  static readonly namespace = 'builderComponent';

  @ApiConfig(Private)
  private dataTransformer = new PublicDataAdapter(this.ps);

  public async getStyleDefinitions(
    virtualManifestId: string,
    childPath: number[],
  ) {
    const api = this.ps.manifestServicesHost.getContextualManifestAPI(
      virtualManifestId,
      childPath,
    );

    return api.resolver.getStyleDefinitions();
  }

  public async getStyles(
    virtualManifestId: string,
    childPath: number[],
  ): Promise<ElementStyles> {
    const api = this.ps.manifestServicesHost.getContextualManifestAPI(
      virtualManifestId,
      childPath,
    );

    return api.query.getStyles();
  }

  public async setStyles(
    virtualManifestId: string,
    childPath: number[],
    values: ElementStylesUpdate,
  ): Promise<void> {
    const api = this.ps.manifestServicesHost.getContextualManifestAPI(
      virtualManifestId,
      childPath,
    );

    return api.query.setStyles(values);
  }

  public async removeStyles(
    virtualManifestId: string,
    childPath: number[],
    values: ElementStylesRemoval,
  ): Promise<void> {
    const api = this.ps.manifestServicesHost.getContextualManifestAPI(
      virtualManifestId,
      childPath,
    );

    return api.query.removeStyles(values);
  }

  public async getDataDefinitions(
    virtualManifestId: string,
    childPath: number[],
  ) {
    const api = this.ps.manifestServicesHost.getContextualManifestAPI(
      virtualManifestId,
      childPath,
    );

    return api.resolver.getDataDefinitions();
  }

  public async getData<T extends ElementData = ElementData>(
    virtualManifestId: string,
    childPath: number[],
  ): Promise<T> {
    const api = this.ps.manifestServicesHost.getContextualManifestAPI(
      virtualManifestId,
      childPath,
    );

    const definitions = await api.resolver.getDataDefinitions();

    return this.dataTransformer.toPublicData(
      await api.query.getData(),
      definitions,
    ) as T;
  }

  public async getResolvedData(
    virtualManifestId: string,
    childPath: number[],
    { dataItemKey }: { dataItemKey: string },
  ) {
    const api = this.ps.manifestServicesHost.getContextualManifestAPI(
      virtualManifestId,
      childPath,
    );

    return api.query.getResolvedData({ dataItemKey });
  }

  public async setData<T extends ElementData = ElementData>(
    virtualManifestId: string,
    childPath: number[],
    values: ElementDataInput<T>,
  ): Promise<void> {
    const api = this.ps.manifestServicesHost.getContextualManifestAPI(
      virtualManifestId,
      childPath,
    );

    const definitions = await api.resolver.getDataDefinitions();

    return api.query.setData(
      this.dataTransformer.fromPublicData(values, definitions),
    );
  }

  public async getPresetDefinitions(
    virtualManifestId: string,
    childPath: number[],
  ) {
    const api = this.ps.manifestServicesHost.getContextualManifestAPI(
      virtualManifestId,
      childPath,
    );

    return api.resolver.getPresetDefinitions();
  }

  public async getAppliedPreset(
    virtualManifestId: string,
    childPath: number[],
  ) {
    const api = this.ps.manifestServicesHost.getContextualManifestAPI(
      virtualManifestId,
      childPath,
    );

    return api.query.getAppliedPreset();
  }

  public async applyPreset(
    virtualManifestId: string,
    childPath: number[],
    options: { key: string },
  ) {
    const api = this.ps.manifestServicesHost.getContextualManifestAPI(
      virtualManifestId,
      childPath,
      OperationSource.SDK,
    );

    return this.ps.ds.transactions.run(async () =>
      api.query.applyPreset(options),
    );
  }

  public async getDisplayGroupDefinitions(
    virtualManifestId: string,
    childPath: number[],
    options?: { filter?: { groupType?: DisplayGroups[string]['groupType'] } },
  ) {
    const api = this.ps.manifestServicesHost.getContextualManifestAPI(
      virtualManifestId,
      childPath,
    );

    return api.resolver.getDisplayGroupDefinitions(options);
  }

  public async getDisplayName(virtualManifestId: string, childPath: number[]) {
    const api = this.ps.manifestServicesHost.getContextualManifestAPI(
      virtualManifestId,
      childPath,
    );

    return api.resolver.getDisplayName();
  }

  public async selectMedia(
    virtualManifestId: string,
    childPath: number[],
    params: ISelectMediaParams,
  ) {
    const addBuilderType = (
      mediaObject: { mediaType: string }[] | undefined,
    ) => {
      if (!mediaObject) {
        return undefined;
      }

      const typeMap = {
        video: 'Builder.Video',
        picture: 'Builder.Image',
        shape: 'Builder.VectorArt',
      };
      return mediaObject.map((media) => ({
        ...media,
        type: typeMap[media.mediaType as keyof typeof typeMap],
      }));
    };

    if ('dataItemKey' in params) {
      const hostMediaSelectorAPI = new HostMediaSelectorAPI(this.ps);

      const [dataDefinitions, data] = await Promise.all([
        this.getDataDefinitions(virtualManifestId, childPath),
        this.getData(virtualManifestId, childPath),
      ]);

      const media = await hostMediaSelectorAPI.selectMedia(params.dataItemKey, {
        dataDefinitions,
        data,
      });

      if (media) {
        await this.setData(virtualManifestId, childPath, {
          [params.dataItemKey]: addBuilderType(media),
        });

        await this.waitForChangesApplied();

        const updatedData = await this.getData(virtualManifestId, childPath);
        return updatedData?.[params.dataItemKey];
      }

      return undefined;
    } else {
      return addBuilderType(await this.ps.hostAPI.editor.selectMedia(params));
    }
  }

  public async selectLink(
    virtualManifestId: string,
    childPath: number[],
    params: ISelectLinkParams,
  ): Promise<ElementLink | null | undefined> {
    if ('dataItemKey' in params) {
      const hostLinkSelectorAPI = new HostLinkSelectorAPI(this.ps);

      const [dataDefinitions, data] = await Promise.all([
        this.getDataDefinitions(virtualManifestId, childPath),
        this.getData(virtualManifestId, childPath),
      ]);

      const link = await hostLinkSelectorAPI.selectLink(
        params.dataItemKey,
        {
          dataDefinitions,
          data,
        },
        params.options,
      );

      if (link) {
        await this.setData(virtualManifestId, childPath, {
          [params.dataItemKey]: this.dataTransformer.toPublicLink(link),
        });

        await this.waitForChangesApplied();

        const updatedData = await this.getData(virtualManifestId, childPath);
        return updatedData?.[params.dataItemKey] as ElementLink | undefined;
      }

      return undefined;
    } else {
      const initialValue = params.value
        ? this.dataTransformer.fromPublicLink(params.value) ?? undefined
        : undefined;
      const link = await this.ps.hostAPI.editor.selectLink(
        initialValue,
        params.options,
      );
      return link ? this.dataTransformer.toPublicLink(link as Link) : link;
    }
  }

  private resolveOverriddenColorPickerOptions(
    virtualManifestId: string,
    params: BasePickerOptions,
  ) {
    const metadata =
      this.ps.manifestServicesHost.getContextualMetadata(virtualManifestId);
    const options = metadata?.overriddenColorPickerOptions;
    const resolvedOptions =
      typeof options === 'function' ? options(params) : options;
    return resolvedOptions ?? {};
  }

  /**
   * TODO: support both cssProperties & cssCustomProperties
   */
  public async selectColor(
    virtualManifestId: string,
    childPath: number[],
    params: ISelectColorParams,
  ) {
    if ('styleItemKey' in params) {
      const styleItemKey = params.styleItemKey;

      const [styleDefinitions, styles] = await Promise.all([
        this.getStyleDefinitions(virtualManifestId, childPath),
        this.getStyles(virtualManifestId, childPath),
      ]);

      await new HostColorSelectorAPI(this.ps).selectColor(params.styleItemKey, {
        styleDefinitions,
        styles,
        onChange: (color) => {
          if (styleItemKey) {
            void this.setStyles(virtualManifestId, childPath, {
              [styleItemKey]: color,
            });
          }
        },
      });

      await this.waitForChangesApplied();

      const updatedStyles = await this.getStyles(virtualManifestId, childPath);
      return (
        updatedStyles?.cssProperties?.[styleItemKey as CssPropertyType] ??
        updatedStyles?.cssCustomProperties?.[styleItemKey]
      );
    } else {
      const overriddenParams = this.resolveOverriddenColorPickerOptions(
        virtualManifestId,
        params,
      );

      const biData: ColorPickerOptions['biData'] = params.biData ?? {};
      biData.editorPointers = this.ps.biUtils.getEditorPointersForBI(
        virtualManifestId,
        childPath,
      );

      return this.ps.hostAPI.editor.selectColor({
        ...params,
        ...overriddenParams,
        biData,
      });
    }
  }

  public async selectBackground(
    virtualManifestId: string,
    childPath: number[],
    params: BackgroundPickerOptions,
  ) {
    const overriddenParams = this.resolveOverriddenColorPickerOptions(
      virtualManifestId,
      params,
    );

    const biData: BackgroundPickerOptions['biData'] = params.biData ?? {};
    biData.editorPointers = this.ps.biUtils.getEditorPointersForBI(
      virtualManifestId,
      childPath,
    );

    return this.ps.hostAPI.editor.selectBackground({
      ...params,
      ...overriddenParams,
    });
  }

  public async selectSort(
    virtualManifestId: string,
    childPath: number[],
    options: ISelectSortParams,
  ): Promise<ElementSort | undefined> {
    try {
      const api = this.ps.manifestServicesHost.getContextualManifestAPI(
        virtualManifestId,
        childPath,
      );

      const metadata =
        this.ps.manifestServicesHost.getContextualMetadata(virtualManifestId);
      const target = metadata?.boundObjectRef ?? {
        editorPointer: api.getEditorPointer(),
      };
      const providerIdentifier = metadata?.boundObjectRef?.contextType;

      return await new HostSortSelectorAPI(this.ps).selectSort(
        options.dataItemKey,
        {
          ...(providerIdentifier ? { providerIdentifier } : {}),
          target,
          onChange: async (nextValue: ElementSort) => {
            await this.setData(virtualManifestId, childPath, {
              [options.dataItemKey]: nextValue,
            });

            await this.waitForChangesApplied();

            const updatedData = await this.getData(
              virtualManifestId,
              childPath,
            );

            return updatedData?.[options.dataItemKey] as
              | ElementSort
              | undefined;
          },
        },
      );
    } catch (error) {
      console.error(error);
      throw createEditorPlatformPrivateAPIError(
        EditorPlatformPrivateAPIErrorCode.SelectSortError,
        error instanceof Error ? error.message : 'select sort operation failed',
      );
    }
  }

  public async selectFilter(
    virtualManifestId: string,
    childPath: number[],
    options: ISelectFilterParams,
  ): Promise<ElementFilter | undefined> {
    try {
      const api = this.ps.manifestServicesHost.getContextualManifestAPI(
        virtualManifestId,
        childPath,
      );
      const definitions = await api.resolver.getDataDefinitions();
      const definition = definitions[options.dataItemKey];

      if (definition?.dataType !== DataType.filter) {
        throw new Error(
          `selectFilter data item "${options.dataItemKey}" is not a filter`,
        );
      }

      const metadata =
        this.ps.manifestServicesHost.getContextualMetadata(virtualManifestId);
      const target = metadata?.boundObjectRef ?? {
        editorPointer: api.getEditorPointer(),
      };
      const providerIdentifier = metadata?.boundObjectRef?.contextType;

      return await new HostFilterSelectorAPI(this.ps).selectFilter(
        options.dataItemKey,
        {
          ...(providerIdentifier ? { providerIdentifier } : {}),
          target,
          onChange: async (nextValue: ElementFilter) => {
            await this.setData(virtualManifestId, childPath, {
              [options.dataItemKey]: nextValue,
            });

            await this.waitForChangesApplied();

            const updatedData = await this.getData(
              virtualManifestId,
              childPath,
            );

            return updatedData?.[options.dataItemKey] as
              | ElementFilter
              | undefined;
          },
        },
      );
    } catch (error) {
      console.error(error);
      throw createEditorPlatformPrivateAPIError(
        EditorPlatformPrivateAPIErrorCode.SelectFilterError,
        error instanceof Error
          ? error.message
          : 'select filter operation failed',
      );
    }
  }

  /**
   * TODO: support both cssProperties & cssCustomProperties
   */
  public async selectFont(
    virtualManifestId: string,
    childPath: number[],
    params: ISelectFontParams,
  ) {
    if ('fontStyleItemKey' in params) {
      const hostFontWeightSelectorAPI = new HostFontWeightSelectorAPI(this.ps);

      const [styleDefinitions, styles] = await Promise.all([
        this.getStyleDefinitions(virtualManifestId, childPath),
        this.getStyles(virtualManifestId, childPath),
      ]);

      const font = await hostFontWeightSelectorAPI.selectFont(
        {
          fontStyleItemKey: params.fontStyleItemKey,
        },
        {
          // TODO: we need to call setStyles after font is selected
          onChange: params.onChange,
          styleDefinitions,
          // @ts-expect-error styles is deprecated, type mismatch is expected
          styles: styles.cssProperties,
        },
      );

      if (font) {
        const key = params.fontStyleItemKey!;
        await this.setStyles(virtualManifestId, childPath, {
          [key]: font,
        });

        await this.waitForChangesApplied();

        const updatedStyles = await this.getStyles(
          virtualManifestId,
          childPath,
        );

        return (
          updatedStyles?.cssProperties?.[key as CssPropertyType] ??
          updatedStyles?.cssCustomProperties?.[key]
        );
      }

      return undefined;
    } else {
      return this.ps.hostAPI.editor.selectFontFamily(params);
    }
  }

  public async selectFontWeight(
    virtualManifestId: string,
    childPath: number[],
    params: ISelectFontWeightParams,
  ) {
    if (
      ('fontFamilyStyleItemKey' in params &&
        'fontWeightStyleItemKey' in params) ||
      'fontStyleItemKey' in params
    ) {
      const hostFontWeightSelectorAPI = new HostFontWeightSelectorAPI(this.ps);

      const [styleDefinitions, styles] = await Promise.all([
        this.getStyleDefinitions(virtualManifestId, childPath),
        this.getStyles(virtualManifestId, childPath),
      ]);

      const font = await hostFontWeightSelectorAPI.selectFontWeight(
        {
          fontFamilyStyleItemKey: params.fontFamilyStyleItemKey,
          fontWeightStyleItemKey: params.fontWeightStyleItemKey,
          fontStyleItemKey: params.fontStyleItemKey,
        },
        {
          // TODO: we need to call setStyles after font weight is selected
          onChange: params.onChange,
          styleDefinitions,
          // @ts-expect-error styles is deprecated, type mismatch is expected
          styles: styles.cssProperties,
        },
      );

      if (font) {
        const key = (params.fontWeightStyleItemKey ?? params.fontStyleItemKey)!;
        const value = params.fontWeightStyleItemKey ? font.weight : font;

        await this.setStyles(virtualManifestId, childPath, {
          [key]: value,
        });

        await this.waitForChangesApplied();

        const updatedStyles = await this.getStyles(
          virtualManifestId,
          childPath,
        );
        return (
          updatedStyles?.cssProperties?.[key as CssPropertyType] ??
          updatedStyles?.cssCustomProperties?.[key]
        );
      }

      return undefined;
    } else {
      return this.ps.hostAPI.editor.selectFontWeight(params);
    }
  }

  public async selectTextTheme(
    _virtualManifestId: string, // TODO: EP-6616 implement connected mode
    params: SelectTextThemeOptions,
  ) {
    return this.ps.hostAPI.editor.selectTextTheme(params);
  }

  public async getState(virtualManifestId: string, childPath: number[]) {
    const api = this.ps.manifestServicesHost.getContextualManifestAPI(
      virtualManifestId,
      childPath,
    );

    return api.query.getState();
  }

  public async setState(
    virtualManifestId: string,
    childPath: number[],
    state: string,
  ) {
    const api = this.ps.manifestServicesHost.getContextualManifestAPI(
      virtualManifestId,
      childPath,
    );

    return api.query.setState(state);
  }

  public async getStateDefinitions(
    virtualManifestId: string,
    childPath: number[],
  ): Promise<NonNullable<EditorElement['states']>> {
    const api = this.ps.manifestServicesHost.getContextualManifestAPI(
      virtualManifestId,
      childPath,
    );

    return api.resolver.getStateDefinitions();
  }

  public async getDesignStateDefinitions(
    virtualManifestId: string,
    childPath: number[],
  ): Promise<NonNullable<EditorElement['states']>> {
    const api = this.ps.manifestServicesHost.getContextualManifestAPI(
      virtualManifestId,
      childPath,
    );

    return api.resolver.getDesignStateDefinitions();
  }

  public async getArrayItemsSelectedIndex(
    manifestContextId: string,
    childPath: number[],
    options?: { arrayItemsDisplayGroupKey?: string },
  ) {
    const api = this.ps.manifestServicesHost.getContextualManifestAPI(
      manifestContextId,
      childPath,
    );

    return this.ps.manifestServicesHost
      .getComponentsService()
      .arrayItems.getArrayItemsSelectedIndex(
        api,
        options?.arrayItemsDisplayGroupKey,
      );
  }

  public async setArrayItemsSelectedIndex(
    manifestContextId: string,
    childPath: number[],
    options: { index: number; arrayItemsDisplayGroupKey?: string },
  ) {
    const api = this.ps.manifestServicesHost.getContextualManifestAPI(
      manifestContextId,
      childPath,
    );

    return this.ps.manifestServicesHost
      .getComponentsService()
      .arrayItems.setArrayItemsSelectedIndex(
        api,
        options.index,
        options.arrayItemsDisplayGroupKey,
      );
  }

  public async resetArrayItemsSelectedIndex(
    manifestContextId: string,
    childPath: number[],
    options?: { arrayItemsDisplayGroupKey?: string },
  ) {
    const api = this.ps.manifestServicesHost.getContextualManifestAPI(
      manifestContextId,
      childPath,
    );

    return this.ps.manifestServicesHost
      .getComponentsService()
      .arrayItems.resetArrayItemsSelectedIndex(
        api,
        options?.arrayItemsDisplayGroupKey,
      );
  }

  /**
   * @deprecated
   * keep for backward compatibility
   */
  onContextualManifestChange(...args: any) {
    // @ts-expect-error xxx
    return this.onChange(...args);
  }

  onChange(
    virtualManifestId: string,
    _childPath: number[],
    cb: (patch: {
      type:
        | 'styles'
        | 'data'
        | 'state'
        | 'preset'
        | 'props'
        | 'manifest'
        | 'bindings'
        | 'providers'
        | 'global'
        | 'global-data'
        | 'global-styles'
        | 'global-state'
        | 'global-preset'
        | 'global-props'
        | 'global-bindings'
        | 'global-providers'
        | 'invalidate'
        | 'design-state';
      value: unknown;
    }) => void,
  ) {
    const virtualManifest =
      this.ps.manifestServicesHost.getContextualManifest(virtualManifestId);

    if (!virtualManifest) {
      return () => {};
    }

    const editorPointers = virtualManifest.manifests.map(
      (x) => x.editorPointer,
    );

    return this.ps.manifestServicesHost.subscribeOnComponentChange(
      (patch) => {
        let patchType: Parameters<typeof cb>[0]['type'] = patch.type;
        if (patch.type === 'global-bindings') {
          patchType = 'bindings';
        } else if (patch.type === 'global-providers') {
          patchType = 'providers';
        }

        cb({
          type: patchType,
          value: patch.value,
        });
      },
      {
        editorPointers,
      },
    );
  }

  public async getBiToken(virtualManifestId: string, childPath: number[]) {
    const editorPointers = this.ps.biUtils.getEditorPointersForBI(
      virtualManifestId,
      childPath,
    );

    return editorPointers
      .map(
        (editorPointer) =>
          this.ps.platformPackagesBridge.EditorPointerAPI?.getDocumentPointer(
            editorPointer,
          )?.id,
      )
      .filter(Boolean)
      .sort()
      .join('__');
  }
}
